/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useMemo } from 'react';
import Service from '@/pages/dashboard/components/PageContainer/Service';
import { BaseServiceComponentProps } from '@/pages/benchmark/types/commom';
import dateTimeFormat from '@home/utils/dbDataFormat/dateTimeFormat';
import { getCompleteDateValue } from '@/pages/dashboard/utils/utils';
import { DATA_TYPE_OBJECT } from '@home/utils/common';
import generateDateRanges from '@home/utils/dbDataFormat/generateDateRanges';
import moment from 'moment';

export interface BIIntelligenceMetricService extends BaseServiceComponentProps {
  metric: string;
  gameCodes: string[];
  [key: string]: any;
}

export default function BIIntelligenceMetricService(props: BIIntelligenceMetricService) {
  const {
    style,
    className,
    dataKey = 'CommonData',
    api,
    children,
    filter,
    metrics,
    gameCodes,
    extraParams = {},
    formatData: propsFormatData,
    data,
    filterConfig,
    paymentType,
    metricKey,
    dayOffset,
    changeData,
    getLatestData,
    subtitleKey,
    rangeDataKey,
    needMaxValue,
    ...restProps
  } = props || {};

  const filterVal = useMemo(() => {
    // 筛选器日期单独偏移
    if (typeof dayOffset === 'number' && !filter?.isChange) {
      const baseStart = moment(filter?.start_time).add(dayOffset, 'days').startOf('day');
      const baseEnd = moment(filter?.end_time).add(dayOffset, 'days').endOf('day');
      changeData(subtitleKey,baseEnd.format('YYYY-MM-DD'));
  
      return {
        ...filter,
        start_time: baseStart.format('YYYY-MM-DD HH:mm:ss'),
        end_time: baseEnd.format('YYYY-MM-DD HH:mm:ss'),
      };
    }
    changeData(subtitleKey, '');
    return { ...filter };
  }, [filter, dayOffset]);
  

  const formatData = (data: any, filterVal: any) => {
    if (propsFormatData && typeof propsFormatData === 'function') {
      return propsFormatData?.(data, filterVal);
    }
  
    const { metric_value_list, sum_avg } = data?.[0] || {};
    const card_list: any[] = [];
    let trend_list: any[] = [];
    let dateRange: string[] = [];
  
    if (metric_value_list?.length > 0) {
      const { start_time, end_time, date_type, isSingle, isChange } = filterVal || {};
      // 单日补齐14天逻辑
      let startDate = dateTimeFormat(start_time, 'day');
      const time_type = DATA_TYPE_OBJECT[date_type];
      // 补齐逻辑
      if (isSingle) {
        const rangeTimeArr = getCompleteDateValue([start_time, end_time], time_type, { offsetDay: 5 });
        startDate = dateTimeFormat(rangeTimeArr[0], 'day');
      }
  
      // 补齐日期和排序
      dateRange = generateDateRanges(startDate, end_time);
      // 性能优化：预处理数据映射，避免重复查找
      const metricValueMap = new Map<string, any>();
      metric_value_list.forEach((item: any) => {
        if (item?.date) {
          metricValueMap.set(item.date, item);
        }
      });
      const sumAvgMap = new Map<string, any>();
      if (sum_avg?.length > 0) {
        sum_avg.forEach((item: any) => {
          if (item?.date) {
            sumAvgMap.set(item.date, item);
          }
        });
      }
      // 默认展示最新数据的逻辑优化
      if (!isChange && getLatestData) {
        // 性能优化：避免多次排序，直接获取有效日期并排序
        const validDates = metric_value_list
          .map((item: { date: string; }) => item.date)
          .filter(Boolean); // 去掉可能为 undefined 的值
        if (validDates.length > 0) {
          // 一次性排序并取值
          validDates.sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
          dateRange = validDates.slice(0, 6).reverse(); // reverse() 比再次排序更高效
          // 展示最近数据日期
          changeData(subtitleKey, dateRange[dateRange.length - 1] || '');
        }
      }
      // 更改日期筛选器后，重置最新数据日期
      if (isChange) {
        changeData(subtitleKey, '');
      }
      // 性能优化：预计算最大值，避免在每次循环中查找
      let maxValueItem = null;
      let cardValue = null;
      
      if (needMaxValue && Array.isArray(metric_value_list)) {
        // 使用 reduce 一次遍历找到最大值，比 for 循环更函数式
        maxValueItem = metric_value_list.reduce(
          (max, item) =>
            typeof item?.value === 'number' &&
            item.value > (max?.value || -Infinity)
              ? item
              : max,
          null,
        );
        
        if (!isSingle && maxValueItem) {
          changeData(subtitleKey, maxValueItem.date);
        }
      }
      // 性能优化：预计算 cardValue，避免在循环中重复计算
      if (!isSingle) {
        if (needMaxValue) {
          cardValue = maxValueItem?.value;
        } else {
          const rangeItem = sumAvgMap.get(rangeDataKey);
          cardValue = rangeItem?.metrics?.[0]?.value;
        }
      }
      trend_list = dateRange?.map((dateString: string, index: number) => {
        const metricItem = metricValueMap.get(dateString) || {};
        const { value, growth_ratio, date } = metricItem;
        // 计算当前项的 cardValue
        const currentCardValue = isSingle ? value : cardValue;
        // 最新一天处理
        if (index === dateRange?.length - 1) {
          card_list.push({
            metrics_name: metricKey,
            value: currentCardValue,
            dod: {
              show_value: growth_ratio,
              this_time: date || dateString,
            },
          });
        }
  
        return {
          date: date || dateString,
          metrics: {
            [metricKey]: {
              value,
              last_value: '',
              last_month_value: '',
              last_year_value: '',
            },
          },
        };
      }) || [];
    }
  
    return {
      card_list,
      trend_list,
    };
  };

  return (
    <div className={className} style={style}>
      <Service
        api={api}
        data={{ ...props.data }}
        dataKey={dataKey}
        filterLoading={false}
        formatData={formatData}
        filterVal={filterVal}
        trackData={[]}
        beforeRequest={(data) => {
          const { filterVal } = data;
          const { start_time, end_time, isSingle, date_type, isChange } = filterVal || {};
          let startDate = dateTimeFormat(start_time, 'day');
          const time_type = DATA_TYPE_OBJECT[date_type];
          // 补齐逻辑
          if (isSingle) {
            const rangeTimeArr = getCompleteDateValue([start_time, end_time], time_type, { offsetDay: 5 });
            startDate = dateTimeFormat(rangeTimeArr[0], 'day');
          }
          // 情报获取最新数据不需要传日期
          const params = {
            start_date: (!isChange && getLatestData) ? "" : startDate,
            end_date: (!isChange && getLatestData) ? "" : dateTimeFormat(end_time, 'day'),
            entity_type: 'combine',
            region_type: 'market',
            metric_condition: {
              mscience: {
                platforms: ['PC', 'PlayStation', 'Xbox', 'Nintendo'],
                segments: ['All'],
              },
              gsd: {
                device: [
                  'PC',
                  'PlayStation 4',
                  'PlayStation 5',
                  'Xbox One',
                  'Xbox Series X|S',
                  'Nintendo Switch',
                ],
                gsd_types: ['NETWORK'],
              },
              vginsights: {
                platforms: ['PC'],
              },
              npd: {
                device: [
                  'PC',
                  'PlayStation 4',
                  'PlayStation 5',
                  'Xbox One',
                  'Xbox Series X|S',
                  'Nintendo Switch',
                  'WII U',
                ],
              },
              newzoo: {
                device: [
                  'PC',
                  'PlayStation 4',
                  'PlayStation 5',
                  'Xbox One',
                  'Xbox Series X|S',
                  'Nintendo Switch',
                ],
              },
              ampere: {
                platforms: ['PC', 'PlayStation', 'Xbox'],
              },
            },
            segmentsByDownload: {
              mscience: [
                'Full Game',
                'MTX',
                'DLC: All Other',
                'DLC: Expansion',
                'DLC: Unlockable Content Pass',
                'DLC: Content Pre-Purchase Pass',
                'Subscription',
              ],
            },
            markets: ['global'],
            granularity: 'daily',
            segments: ['All'],
            platforms: ['PC', 'PlayStation', 'Xbox', 'Nintendo'],
            b_group_by_date: true,
            b_growth_ratio: true,
            b_sum_avg_show: true,
            b_group_by_market: false,
            b_group_by_region: false,
            b_group_by_platform: false,
            b_group_by_segment: false,
            start_release_time: 0,
            end_release_time: 0,
            ...extraParams,
          };

          return params;
        }}
      >
        {React.Children.map(children, (child: any) =>
          React.cloneElement(child, {
            ...(restProps || {}),
            ...data,
            filter: filterVal,
          }),
        )}
      </Service>
    </div>
  );
}
